import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowLeft, ShieldCheck, FileText, Scale, Languages, Info } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";

const SECTIONS = [
  {
    slug: "privacy-notice",
    label: "Privacy Notice",
    sub: "DPDP Act, 2023",
    icon: ShieldCheck,
  },
  {
    slug: "privacy-policy",
    label: "Privacy Policy",
    sub: "How we use your data",
    icon: FileText,
  },
  {
    slug: "terms-of-service",
    label: "Terms of Service",
    sub: "Rules of using Careable",
    icon: Scale,
  },
];

const LANG_LABELS = {
  en: "English",
  hi: "Hindi · हिन्दी",
  ml: "Malayalam · മലയാളം",
  ta: "Tamil · தமிழ்",
  kn: "Kannada · ಕನ್ನಡ",
};

export default function TermsAndPolicies() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [docs, setDocs] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const initialSlug = useMemo(() => {
    const hash = (location.hash || "").replace("#", "");
    return SECTIONS.find((s) => s.slug === hash)?.slug || "privacy-notice";
  }, [location.hash]);

  const initialLang = (searchParams.get("lang") || "en").toLowerCase();
  const [active, setActive] = useState(initialSlug);
  const [lang, setLang] = useState(
    LANG_LABELS[initialLang] ? initialLang : "en"
  );

  useEffect(() => {
    document.title = "Terms & Policies · Careable 360+ Plus";
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/legal/languages");
        if (!cancelled) setLanguages(data || []);
      } catch (err) {
        if (!cancelled) {
          // Languages endpoint failure is non-fatal — keep an English-only fallback.
          setLanguages([{ code: "en", label: "English", native: "English" }]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/legal", { params: { lang } });
        if (!cancelled) {
          setDocs(data || []);
          setError("");
        }
      } catch (err) {
        if (!cancelled)
          setError(err?.response?.data?.detail || err.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const current = useMemo(
    () => docs.find((d) => d.slug === active),
    [docs, active]
  );

  const updateActive = (slug) => {
    setActive(slug);
    navigate({ pathname: "/terms-and-policies", search: searchParams.toString(), hash: `#${slug}` }, { replace: true });
    const el = document.getElementById("legal-content");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const updateLang = (code) => {
    setLang(code);
    const next = new URLSearchParams(searchParams);
    if (code === "en") next.delete("lang");
    else next.set("lang", code);
    setSearchParams(next, { replace: true });
  };

  return (
    <div
      data-testid="terms-page"
      className="min-h-screen bg-[#F4FAF8] text-[#0E2A36]"
    >
      <header className="bg-[#1F4E5F] text-[#F4FAF8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <Link
            data-testid="terms-back-home"
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-[#F4FAF8]/75 hover:text-[#F4FAF8] mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <p className="text-[11px] font-bold tracking-[0.22em] text-[#E8A33D] uppercase">
            Legal
          </p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-2 leading-tight">
            Terms &amp; Policies
          </h1>
          <p className="mt-4 text-[#F4FAF8]/80 max-w-2xl text-[15px] leading-relaxed">
            Transparency about how we handle your data and what you can expect
            from Careable 360+ Plus — written in plain language, aligned with
            India&apos;s Digital Personal Data Protection Act, 2023.
          </p>

          {languages.length > 1 && (
            <div className="mt-6">
              <p className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.22em] text-[#F4FAF8]/65 uppercase mb-2">
                <Languages className="w-3 h-3" /> Read in
              </p>
              <div
                data-testid="terms-lang-switcher"
                className="flex flex-wrap gap-1.5"
              >
                {languages.map((l) => {
                  const isActive = l.code === lang;
                  const anyAvailable = Object.values(l.available || {}).some(Boolean);
                  return (
                    <button
                      key={l.code}
                      data-testid={`terms-lang-${l.code}`}
                      onClick={() => updateLang(l.code)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all border ${
                        isActive
                          ? "bg-[#E8A33D] text-[#1F4E5F] border-[#E8A33D] shadow-sm"
                          : anyAvailable || l.code === "en"
                          ? "bg-white/10 text-[#F4FAF8] border-white/20 hover:bg-white/15"
                          : "bg-white/5 text-[#F4FAF8]/55 border-white/10 hover:bg-white/10"
                      }`}
                      title={
                        l.code === "en" || anyAvailable
                          ? l.label
                          : `${l.label} — translation in progress`
                      }
                    >
                      <span>{l.native}</span>
                      {!isActive && l.code !== "en" && !anyAvailable && (
                        <span className="text-[9px] uppercase tracking-wider opacity-70">
                          soon
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <div className="grid lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-4">
            <nav
              data-testid="terms-tabs"
              className="lg:sticky lg:top-24 flex lg:flex-col gap-2 overflow-x-auto"
            >
              {SECTIONS.map((s) => {
                const isActive = s.slug === active;
                const Icon = s.icon;
                return (
                  <button
                    key={s.slug}
                    data-testid={`terms-tab-${s.slug}`}
                    onClick={() => updateActive(s.slug)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-left transition-all shrink-0 border ${
                      isActive
                        ? "bg-[#1F4E5F] text-[#F4FAF8] border-[#1F4E5F] shadow-sm"
                        : "bg-white text-[#0E2A36] border-[#C9DDD6] hover:border-[#1F4E5F]"
                    }`}
                  >
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                        isActive ? "bg-white/15" : "bg-[#E1EEEA]"
                      }`}
                    >
                      <Icon
                        className={`w-4 h-4 ${
                          isActive ? "text-[#F4FAF8]" : "text-[#1F4E5F]"
                        }`}
                      />
                    </div>
                    <div className="leading-tight">
                      <p className="font-semibold text-sm">{s.label}</p>
                      <p
                        className={`text-[11px] mt-0.5 ${
                          isActive ? "text-[#F4FAF8]/80" : "text-[#6A8290]"
                        }`}
                      >
                        {s.sub}
                      </p>
                    </div>
                  </button>
                );
              })}
            </nav>
          </aside>

          <section id="legal-content" className="lg:col-span-8">
            <article
              data-testid="terms-content"
              className="bg-white border border-[#C9DDD6] rounded-3xl p-6 sm:p-8 lg:p-10 shadow-[0_2px_18px_rgba(15,56,32,0.04)]"
            >
              {loading ? (
                <div className="py-20 flex items-center justify-center text-[#6A8290] text-sm">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
              ) : error ? (
                <div
                  data-testid="terms-error"
                  className="py-16 text-center text-[#C84444] text-sm"
                >
                  {error}
                </div>
              ) : !current ? (
                <div className="py-16 text-center text-[#6A8290] text-sm">
                  Document not available.
                </div>
              ) : (
                <>
                  {current.is_fallback && lang !== "en" && (
                    <div
                      data-testid="terms-fallback-banner"
                      className="mb-5 rounded-2xl border border-[#E8A33D]/40 bg-[#FBF1DC] text-[#7A571A] px-4 py-3 text-[13px] leading-relaxed flex items-start gap-2.5"
                    >
                      <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#B27818]" />
                      <span>
                        We&apos;re showing the English version because the{" "}
                        <strong>{LANG_LABELS[lang]?.split(" · ")[0]}</strong>{" "}
                        translation isn&apos;t ready yet. Our team is preparing it
                        carefully — check back soon.
                      </span>
                    </div>
                  )}
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0E2A36] leading-tight">
                    {current.title}
                  </h2>
                  <p className="mt-2 text-xs text-[#6A8290]">
                    Last updated:{" "}
                    {new Date(current.updated_at).toLocaleDateString(undefined, {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                  <div
                    data-testid="terms-markdown"
                    className="prose prose-careable mt-6 max-w-none"
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{current.content}</ReactMarkdown>
                  </div>
                </>
              )}
            </article>
          </section>
        </div>
      </main>
    </div>
  );
}
